import { Form , Row , Col , DatePicker , Button , Table, message, Modal, Input} from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getRangeOptions } from './reports.module';
import { getWorkFlowAction } from '../../actions/reports.action';
import { getActiveOperatorsWithConfigs } from '../../actions/admin.action';
import { getAPIDateFormat, getLocalDateAndTime } from "../../utils/date.utils";
import { loadingAction } from "../../actions/loading-actions";
import { roundNumber } from "../../utils/math.utils";
import * as XLSX from 'xlsx-js-style';
import { isValid, isValidArray, isValidNumber, isValidString } from "../../utils/utilities";
import { pushUsage , pushSettlementUsage} from "../../actions/sku.actions";
import { getPartnerActivityTotals } from "../../utils/ota-partner.util";

const { RangePicker } = DatePicker;
let allowedMaxDaysRange=31;

/**
 * Merge workFlowSummary with activeOperators: for each activeOperator not present
 * in workFlowSummary (by operatorCode vs operator_code), add a row with operator
 * info from activeOperators and all numeric/other values as 0.
 */
const mergeWorkFlowSummaryWithActiveOperators = (workFlowSummary, activeOperators) => {
    if (!isValidArray(activeOperators)) return isValidArray(workFlowSummary) ? workFlowSummary : [];
    let insertedOperatorCodes = [];
    let updatedWorkFlowSummary = [];
    workFlowSummary.forEach((item) => {
        insertedOperatorCodes.push(item.operatorCode);
        let updatedItem = {...item};
        let operatorData = activeOperators.find((op) => op.operator_code === item.operatorCode);
        let travelsAppConfig = operatorData?.travels_app_config;
        let websiteAgent = operatorData?.website_agent;
        let smsCharge = 0.20, whatsappCharge = 0.25, gpsCharge = 550, copilotTabsCharge = 550;
        let billingType = "Trip Based", stdTripCharge = 35, ssTripCharge = 100, otaPercent = 1, monthlyMaintenanceCharge = 1000;
        if (isValid(travelsAppConfig)) {
            if (isValid(travelsAppConfig.GPS_DEVICE_NUM)) {
                updatedItem['noOfGpsDevices'] = Number(travelsAppConfig.GPS_DEVICE_NUM);
                if (isValid(travelsAppConfig.MONTHLY_GPS_CHARGE)) {
                    gpsCharge = Number(travelsAppConfig.MONTHLY_GPS_CHARGE);
                }
                updatedItem['gpsMonthlyCharge'] = gpsCharge*Number(travelsAppConfig.GPS_DEVICE_NUM);
            }
            // CoPilot device count is server-computed as min(driver-app
            // subscriptions, buses); COPILOT_TABS config is no longer the source.
            if (isValid(travelsAppConfig.MONTHLY_COPILOT_CHARGE)) {
                copilotTabsCharge = Number(travelsAppConfig.MONTHLY_COPILOT_CHARGE);
            }
            if (isValidNumber(travelsAppConfig.SMS_CHARGE)) {
                smsCharge = Number(travelsAppConfig.SMS_CHARGE);
            }
            if (isValidNumber(travelsAppConfig.WHATSAPP_CHARGE)) {
                whatsappCharge = Number(travelsAppConfig.WHATSAPP_CHARGE);
            }
            if (isValidString(travelsAppConfig.BILLING_TYPE)) {
                billingType = travelsAppConfig.BILLING_TYPE;
            }
            if (isValidNumber(travelsAppConfig.STD_TRIP_CHARGE)) {
                stdTripCharge = Number(travelsAppConfig.STD_TRIP_CHARGE);
            }
            if (isValidNumber(travelsAppConfig.SEAT_SHARING_TRIP_CHARGE)) {
                ssTripCharge = Number(travelsAppConfig.SEAT_SHARING_TRIP_CHARGE);
            }
            if (isValidNumber(travelsAppConfig.OTA_PERCENT)) {
                otaPercent = Number(travelsAppConfig.OTA_PERCENT);
            }
            if (isValidNumber(travelsAppConfig.MONTHLY_MAINTENANCE_CHARGE)) {
                monthlyMaintenanceCharge = Number(travelsAppConfig.MONTHLY_MAINTENANCE_CHARGE);
            }
        }
        if (isValid(websiteAgent)) {
            let webCommissionPercentage = Number(websiteAgent.commission_percentage);
            // Website Commission is in GST inclusive, so we need to convert it to GST exclusive, GST is 18%, so we divide by 1.18 to get the GST exclusive value.
            webCommissionPercentage = webCommissionPercentage/1.18;
            updatedItem['websiteCommission'] = webCommissionPercentage;
        }
        updatedItem['tripBasedCharge'] = monthlyMaintenanceCharge;
        updatedItem['otaCharge'] = 0;
        updatedItem["billingType"] = billingType;
        if (billingType == "Trip Based") {
            updatedItem['tripBasedCharge']+= (((updatedItem.serviceCounts || 0) - (updatedItem.seatSharingServiceCount || 0))*stdTripCharge) + ((updatedItem.seatSharingServiceCount || 0)*ssTripCharge);
        }
        else {
            updatedItem['otaCharge'] = ((updatedItem.otaConfirmedFare || 0)* otaPercent/100);
        }
        updatedItem['smsUsageCharge'] = smsCharge*updatedItem.smsCount;
        updatedItem['whatsappUsageCharge'] = whatsappCharge*updatedItem.whatsAppSmsCount;
        updatedItem['gpsCharge'] = gpsCharge;
        updatedItem['copilotTabsCharge'] = copilotTabsCharge;
        updatedItem['noOfCopilotTabs'] = Number(item.copilotDeviceCount) || 0;
        updatedItem['copilotTabsMonthlyCharge'] = copilotTabsCharge * (Number(item.copilotDeviceCount) || 0);
        updatedItem['smsCharge'] = smsCharge;
        updatedItem['whatsappCharge'] = whatsappCharge;
        updatedItem['otaPercent'] = otaPercent;
        updatedItem['monthlyMaintenanceCharge'] = monthlyMaintenanceCharge;
        updatedItem['stdTripCharge'] = stdTripCharge;
        updatedItem['ssTripCharge'] = ssTripCharge;
        updatedWorkFlowSummary.push(updatedItem);
    });
    let merged = [...updatedWorkFlowSummary];
    activeOperators.forEach((op) => {
        const code = op.operator_code;
        if (code && !insertedOperatorCodes.includes(code)) {
            let monthlyMaintenanceCharge = 1000, gpsCharge = 550, copilotTabsCharge = 550, noOfGpsDevices = 0, noOfCopilotTabs = 0;
            let gpsMonthlyCharge = 0, copilotTabsMonthlyCharge = 0, billingType = "Trip Based", smsCharge = 0.20, whatsappCharge = 0.25, otaPercent = 1;
            let stdTripCharge = 35, ssTripCharge = 100, websiteCommission = 4;
            if (isValid(op.travels_app_config)) {
                let travelsAppConfig = op.travels_app_config;
                if (isValidNumber(travelsAppConfig.MONTHLY_MAINTENANCE_CHARGE)){
                    monthlyMaintenanceCharge = Number(travelsAppConfig.MONTHLY_MAINTENANCE_CHARGE);
                }
                if (isValid(travelsAppConfig.GPS_DEVICE_NUM)) {
                    noOfGpsDevices = Number(travelsAppConfig.GPS_DEVICE_NUM);
                    if (isValid(travelsAppConfig.MONTHLY_GPS_CHARGE)) {
                        gpsCharge = Number(travelsAppConfig.MONTHLY_GPS_CHARGE);
                    }
                    gpsMonthlyCharge = gpsCharge*noOfGpsDevices;
                }
                // No workflow activity this period -> no buses ran -> 0 CoPilot
                // devices (count/charge stay 0). Keep the rate for consistency.
                if (isValid(travelsAppConfig.MONTHLY_COPILOT_CHARGE)) {
                    copilotTabsCharge = Number(travelsAppConfig.MONTHLY_COPILOT_CHARGE);
                }
                if (isValid(travelsAppConfig.BILLING_TYPE)) {
                    billingType = travelsAppConfig.BILLING_TYPE;
                }
                if (isValidNumber(travelsAppConfig.SMS_CHARGE)) {
                    smsCharge = Number(travelsAppConfig.SMS_CHARGE);
                }
                if (isValidNumber(travelsAppConfig.WHATSAPP_CHARGE)) {
                    whatsappCharge = Number(travelsAppConfig.WHATSAPP_CHARGE);
                }
                if (isValidNumber(travelsAppConfig.OTA_PERCENT)) {
                    otaPercent = Number(travelsAppConfig.OTA_PERCENT);
                }
                if (isValidNumber(travelsAppConfig.STD_TRIP_CHARGE)) {
                    stdTripCharge = Number(travelsAppConfig.STD_TRIP_CHARGE);
                }
                if (isValidNumber(travelsAppConfig.SEAT_SHARING_TRIP_CHARGE)) {
                    ssTripCharge = Number(travelsAppConfig.SEAT_SHARING_TRIP_CHARGE);
                }
                if (isValid(op.website_agent)) {
                    websiteCommission = Number(op.website_agent.commission_percentage);
                    websiteCommission = websiteCommission/1.18;
                }
            }
            merged.push({
                operatorCode: code,
                operatorNumber: op.operator_number,
                operatorName: op.name,
                whatsappEnabled: !!op.whatsapp_message,
                confirmedAmount: 0,
                cancelledAmount: 0,
                cancellationPenality: 0,
                serviceCounts: 0,
                seatSharingServiceCount: 0,
                smsCount: 0,
                smsQty: 0,
                whatsAppSmsCount: 0,
                mavenConfirmedFare: 0,
                mavenCancellationPenality: 0,
                otaConfirmedFare: 0,
                driverSubscriptions: 0,
                uniqueVechicleIds: [],
                tripBasedCharge: monthlyMaintenanceCharge,
                gpsCharge: gpsCharge,
                copilotTabsCharge: copilotTabsCharge,
                noOfGpsDevices: noOfGpsDevices,
                noOfCopilotTabs: noOfCopilotTabs,
                gpsMonthlyCharge: gpsMonthlyCharge,
                copilotTabsMonthlyCharge: copilotTabsMonthlyCharge,
                billingType: billingType,
                smsCharge: smsCharge,
                whatsappCharge: whatsappCharge,
                otaPercent: otaPercent,
                websiteCommission: websiteCommission,
                stdTripCharge: stdTripCharge,
                ssTripCharge: ssTripCharge,
                monthlyMaintenanceCharge: monthlyMaintenanceCharge
            });
        }
    });
    return merged;
};

const generateJsonToExport=(filters,workFlowSummary, invoiceStartNumber)=>{
    // Sort by operatorNumber before exporting: rows with operatorNumber first (by value), rows with no value last
    const sortedWorkFlowSummary = isValidArray(workFlowSummary)
        ? [...workFlowSummary].sort((a, b) => {
            const aHasNumber = a.operatorNumber != null && a.operatorNumber !== '';
            const bHasNumber = b.operatorNumber != null && b.operatorNumber !== '';
            if (aHasNumber && !bHasNumber) return -1;
            if (!aHasNumber && bHasNumber) return 1;
            if (!aHasNumber && !bHasNumber) return 0;
            return (Number(a.operatorNumber) || 0) - (Number(b.operatorNumber) || 0);
          })
        : [];

    // Extract base number from invoice start (e.g., "INV1" -> 1, "INV467" -> 467)
    const extractNumber = (invoiceStr) => {
        const match = invoiceStr.match(/(\d+)$/);
        return match ? parseInt(match[1], 10) : 1;
    };
    
    const baseNumber = extractNumber(invoiceStartNumber);
    const prefix = invoiceStartNumber.replace(/\d+$/, ''); // Get prefix (e.g., "INV" from "INV1")
    
    // Generate invoice number for a given index
    const generateInvoiceNumber = (index) => {
        return `${prefix}${baseNumber + index}`;
    };

    let reportsDemandDataXmlData=[];
    let totalServiceCounts = 0, totalSeatSharingServiceCount=0, totalSmsQty = 0;
    let totalSmsCount = 0, totalCancelledAmount = 0, totalConfirmedAmount = 0,totalcancellationPenality=0;
    let totalTripsWithoutSeatSharing = 0;
    let totalSmsUsageCharge = 0;
    let totalWhatsappUsageCharge = 0;
    let totalGpsMonthlyCharge = 0;
    let totalCopilotTabsMonthlyCharge = 0;
    let totalOtaConfirmedFare = 0;
    let totalMavenCancellationPenality = 0;
    
    if(isValidArray(sortedWorkFlowSummary)&&sortedWorkFlowSummary.length>0){
        sortedWorkFlowSummary.forEach((element,index) => {
            let tripsWithoutSeatSharing = (element.serviceCounts || 0) - (element.seatSharingServiceCount || 0);
            totalServiceCounts+= element.serviceCounts;
            totalSeatSharingServiceCount+= element.seatSharingServiceCount;
            totalTripsWithoutSeatSharing+= tripsWithoutSeatSharing;
            totalSmsQty+= element.smsQty;
            totalSmsCount+= element.smsCount;
            totalCancelledAmount+= element.cancelledAmount;
            totalConfirmedAmount+= element.confirmedAmount;
            totalcancellationPenality+= element.cancellationPenality;
            totalSmsUsageCharge+= element.smsUsageCharge || 0;
            totalWhatsappUsageCharge+= element.whatsappUsageCharge || 0;
            totalGpsMonthlyCharge+= element.gpsMonthlyCharge || 0;
            totalCopilotTabsMonthlyCharge+= element.copilotTabsMonthlyCharge || 0;
            totalOtaConfirmedFare+= element.otaConfirmedFare || 0;
            totalMavenCancellationPenality+= element.mavenCancellationPenality || 0;
            
            const invoiceNumber = generateInvoiceNumber(index);

            reportsDemandDataXmlData.push([
                element.operatorName || '',                    // A: Customer Name
                element.operatorCode || '',                    // B: Operator Code
                invoiceNumber,                                  // C: Invoice Number
                0,                                            // D: QwikBus Website Setup Charges (empty)
                0,                                            // E: Domain Renewal Charges (empty)
                element.noOfGpsDevices || 0,                   // F: Number of GPS
                0,                                            // G: GPS Device (empty)
                '',                                            // H: GPS Monthly platform charges (will be set as formula)
                element.noOfCopilotTabs || 0,                  // I: Co-pilot Tabs
                0,                                            // J: Tab Charges (empty)
                '',                                            // K: Co-Pilot App Maintenance Charges (will be set as formula)
                roundNumber(element.confirmedAmount || 0),      // L: Confirmed Ticket amt
                roundNumber(element.cancelledAmount || 0),     // M: Canceled ticket amt
                roundNumber(element.cancellationPenality || 0), // N: Cancellation Charges
                '',                                            // O: Charges for Web Tickets Sales (will be set as formula)
                (element.serviceCounts || 0) - (element.seatSharingServiceCount || 0), // P: Active Services
                element.seatSharingServiceCount || 0,          // Q: SS Trips
                '',                                            // R: Ticket Booking and Inventory Management based on Schedule (will be set as formula)
                element.monthlyMaintenanceCharge || 0,         // S: Monthly Maintenance Charge
                element.smsCount || 0,                          // T: SMS Count
                '',                                            // U: SMS Usage based on count (will be set as formula)
                0,                                            // V: Whatsapp Setup (empty)
                element.whatsAppSmsCount || 0,                 // W: Whatsapp Count
                '',                                            // X: Whatsapp Usage based on count (will be set as formula)
                // Y / AB carry the WHOLE QwikBus program (maven + every other
                // OTA partner) so the invoice stays complete even though the
                // on-screen table breaks partners out into their own columns.
                roundNumber((element.mavenConfirmedFare || 0) + getPartnerActivityTotals(element.partnerActivity).confirmedFare),    // Y: QwikBus 3-rd Party OTA Booked Tickets Value
                '',                                            // Z: 3rd-Party OTA Commission (QwikBus) (will be set as formula)
                '',                                            // AA: 3rd-Party API Charges (will be set as formula)
                roundNumber((element.mavenCancellationPenality || 0) + getPartnerActivityTotals(element.partnerActivity).cancellationPenality),// AB: QwikBus- Online Agents- Cancellation Penalty
                element.billingType != "Trip Based" ? roundNumber(element.otaConfirmedFare || 0) : 0,    // AC: OTA Booking Value
                '',                                            // AD: OTA Booking Charges (will be set as formula)
                '',                                            // AE: Sub-Total (will be set as formula)
                '',                                            // AF: GST (will be set as formula)
                ''                                             // AG: Invoice Amount (will be set as formula)
            ])
        });
    }

    let generatedTime = getLocalDateAndTime(new Date());

    // Headers matching Test.csv format (case insensitive matching)
    const headers = [
        "Customer Name",
        "Operator Code",
        "Invoice Number",
        "QwikBus Website Setup Charges",
        "Domain Renewal Charges",
        "Number of GPS",
        "GPS Device",
        "GPS Monthly platform charges",
        "Co-pilot Tabs",
        "Tab Charges",
        "Co-Pilot App Maintenance Charges",
        "Confirmed Ticket amt",
        "Canceled ticket amt",
        "Cancellation Charges",
        "Charges for Web Tickets Sales",
        "Active Services",
        "SS Trips",
        "Ticket Booking and Inventory Management based on Schedule",
        "Monthly Maintenance Charge",
        "SMS Count",
        "SMS Usage based on count",
        "Whatsapp Setup",
        "Whatsapp Count",
        "Whatsapp Usage based on count",
        "QwikBus 3-rd Party OTA Booked Tickets Value",
        "3rd-Party OTA Commission (QwikBus)",
        "3rd-Party API Charges",
        "QwikBus- Online Agents- Cancellation Penalty",
        "OTA Booking Value",
        "OTA Booking Charges",
        "Sub-Total",
        "GST",
        "Invoice Amount"
    ];

    var worksheet = XLSX.utils.aoa_to_sheet([
        headers,
        ...reportsDemandDataXmlData
    ]);
    
    // Apply wrap text style to all header cells (row 1, 0-indexed)
    const headerRowIndex = 0;
    const headerStyle = {
        alignment: { wrapText: true, vertical: 'center', horizontal: 'center' },
        font: { bold: true },
        fill: { fgColor: { rgb: "D3D3D3" } }
    };
    
    headers.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: colIndex });
        if (worksheet[cellAddress]) {
            worksheet[cellAddress].s = headerStyle;
        } else {
            worksheet[cellAddress] = { v: headers[colIndex], s: headerStyle };
        }
    });
    
    // Set row height for header row (48px ≈ 36 pt; 1px ≈ 0.75pt at 96 DPI)
    if (!worksheet['!rows']) worksheet['!rows'] = [];
    worksheet['!rows'][headerRowIndex] = { hpt: 70 };
    
    // Set column widths: A = 156px (~22 wch), rest = 87px (~12 wch). Excel: wch ≈ (px - 5) / 7
    const colAWidth = 28;   // 22
    const otherColWidth = 12; // 12
    worksheet['!cols'] = headers.map((_, i) => ({
        wch: i === 0 ? colAWidth
            : i === 1 ? 13            // Operator Code
            : i === 2 ? 9             // Invoice Number
            : i === 17 ? 16           // Ticket Booking and Inventory Management based on Schedule
            : i === 18 ? 18           // Monthly Maintenance Charge
            : otherColWidth
    }));
    
    // Set Excel formulas for SubTotal, GST, and Invoice Amount columns
    // Column indices: AE=30 (SubTotal), AF=31 (GST), AG=32 (Invoice Amount)
    // Data rows start at row 2 (1-indexed), which is row index 1 (0-indexed)
    reportsDemandDataXmlData.forEach((_, index) => {
        const dataRowNumber = index + 2; // Excel row number (1-indexed)
        const dataRowIndex = index + 1;  // 0-indexed row in worksheet
        const billingType = sortedWorkFlowSummary[index]?.billingType || '';
        const element = sortedWorkFlowSummary[index];

        // Number format for 2 decimal places
        const numberFormat = {
            numFmt: '0.00'
        };

        // H column (index 7): GPS Monthly platform charges = F * gpsCharge
        const hCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 7 });
        const gpsCharge = element?.gpsCharge || 0;
        worksheet[hCellAddress] = {
            f: `F${dataRowNumber}*${gpsCharge}`,
            s: numberFormat
        };

        // K column (index 10): Co-Pilot App Maintenance Charges = I * copilotTabsCharge
        const kCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 10 });
        const copilotTabsCharge = element?.copilotTabsCharge || 0;
        worksheet[kCellAddress] = {
            f: `I${dataRowNumber}*${copilotTabsCharge}`,
            s: numberFormat
        };

        // Z column (index 25): 3rd-Party OTA Commission = Y * 0.10
        const zCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 25 });
        worksheet[zCellAddress] = {
            f: `Y${dataRowNumber}*0.10`,
            s: numberFormat
        };

        // AA column (index 26): 3rd-Party API Charges = Y * 0.02
        const aaCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 26 });
        worksheet[aaCellAddress] = {
            f: `Y${dataRowNumber}*0.02`,
            s: numberFormat
        };

        // R column (index 17): Ticket Booking and Inventory Management = (P * stdTripCharge) + (Q * ssTripCharge) if Trip Based, else 0. Maintenance is now in column S.
        const rCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 17 });
        const stdTripCharge = element?.stdTripCharge || 0;
        const ssTripCharge = element?.ssTripCharge || 0;
        if (billingType === "Trip Based") {
            worksheet[rCellAddress] = {
                f: `P${dataRowNumber}*${stdTripCharge}+Q${dataRowNumber}*${ssTripCharge}`,
                s: numberFormat
            };
        } else {
            worksheet[rCellAddress] = {
                f: `0`,
                s: numberFormat
            };
        }

        // U column (index 20): SMS Usage based on count = T * smsCharge
        const uCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 20 });
        const smsCharge = element?.smsCharge;
        worksheet[uCellAddress] = {
            f: `T${dataRowNumber}*${smsCharge}`,
            s: numberFormat
        };

        // X column (index 23): Whatsapp Usage based on count = W * whatsappCharge
        const xCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 23 });
        const whatsappCharge = element?.whatsappCharge;
        worksheet[xCellAddress] = {
            f: `W${dataRowNumber}*${whatsappCharge}`,
            s: numberFormat
        };

        // O column (index 14): Charges for Web Tickets Sales = (L + M) * websiteCommission / 100
        const oCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 14 });
        const websiteCommission = (element?.websiteCommission || 4);
        worksheet[oCellAddress] = {
            f: `(L${dataRowNumber}+M${dataRowNumber})*${websiteCommission}/100`,
            s: numberFormat
        };

        // AD column (index 29): OTA Booking Charges = AC * otaPercent / 100 (only if not Trip Based)
        const adCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 29 });
        const otaPercent = element?.otaPercent;
        if (billingType !== "Trip Based") {
            worksheet[adCellAddress] = {
                f: `AC${dataRowNumber}*${otaPercent}/100`,
                s: numberFormat
            };
        } else {
            worksheet[adCellAddress] = {
                f: `0`,
                s: numberFormat
            };
        }

        // AE column (index 30): SubTotal — sum of all chargeable columns. R+S together replace the old combined Q.
        const aeCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 30 });
        worksheet[aeCellAddress] = {
            f: `D${dataRowNumber}+O${dataRowNumber}+R${dataRowNumber}+S${dataRowNumber}+U${dataRowNumber}+K${dataRowNumber}+E${dataRowNumber}+Z${dataRowNumber}+AA${dataRowNumber}+J${dataRowNumber}+G${dataRowNumber}+H${dataRowNumber}+V${dataRowNumber}+X${dataRowNumber}+AD${dataRowNumber}`,
            s: numberFormat
        };

        // AF column (index 31): GST = AE * 0.18
        const afCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 31 });
        worksheet[afCellAddress] = {
            f: `AE${dataRowNumber}*0.18`,
            s: numberFormat
        };

        // AG column (index 32): Invoice Amount = AE + AF
        const agCellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: 32 });
        worksheet[agCellAddress] = {
            f: `AE${dataRowNumber}+AF${dataRowNumber}`,
            s: numberFormat
        };
        
        // Highlight row in red if no trip charges accrued (Trip Based row produced only the maintenance charge)
        // OR OTA Booking Charge is less than 1
        let shouldHighlight = false;
        if (billingType == "Trip Based") {
            let tripBasedCharge = (element?.tripBasedCharge || 0)
            let monthlyMaintenanceCharge = (element?.monthlyMaintenanceCharge || 0);
            shouldHighlight = (tripBasedCharge == monthlyMaintenanceCharge) || (tripBasedCharge < 1);
        } else {
            shouldHighlight = (element?.otaCharge || 0) < 1;
        }
        
        if (shouldHighlight) {
            // Red background color style
            const redFill = { fgColor: { rgb: "FFE6E6" } }; // Light red background
            
            // Apply red background to all cells in this row (columns 0-30)
            for (let colIndex = 0; colIndex < headers.length; colIndex++) {
                const cellAddress = XLSX.utils.encode_cell({ r: dataRowIndex, c: colIndex });
                if (worksheet[cellAddress]) {
                    // Merge existing style with red fill, preserving existing styles
                    const existingStyle = worksheet[cellAddress].s || {};
                    worksheet[cellAddress].s = {
                        ...existingStyle,
                        fill: redFill
                    };
                } else {
                    // Create cell with red fill if it doesn't exist
                    worksheet[cellAddress] = { v: '', s: { fill: redFill } };
                }
            }
        }
    });
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Billing Report');
    XLSX.writeFile(workbook, 'billing-report.xlsx');
}

export const CostSummaryReport = (props) => {

    const [form] = Form.useForm();
    const dispatch=useDispatch();
    const [workFlowSummary,setWorkFlowSummary] = useState([]);
    const [showReport,setShowReport]=useState(false);
    const [filters,setFilters]=useState({
        fromDate:undefined,
        toDate:undefined,
    });

    const [activeOperators, setActiveOperators] = useState([]);
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [invoiceStartNumber, setInvoiceStartNumber] = useState('INV1');
    
    useEffect(() => {
        getActiveOperatorsWithConfigs((data)=>{
            setActiveOperators(data);
        },(errMsg)=>{
            console.log({errMsg});
        });
    }, []);


    const onSuccess=(data)=>{
        const merged = mergeWorkFlowSummaryWithActiveOperators(data, activeOperators);
        setWorkFlowSummary(merged);
        setLoading(false);
        setShowReport(true);
    }

    const onFailure=()=>{
        setLoading(false);
    }

    const onFinish=(values)=>{
        let {
            dateRange
        }=values;
        setLoading(true);
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        filters.fromDate=fromDate;
        filters.toDate=toDate;
        setFilters({...filters});
        getWorkFlowAction(fromDate,toDate,onSuccess,onFailure);
    }

    
    const setLoading=(bool)=>{
        dispatch(loadingAction(bool));
    }

    const columns = [
        {
            title:'SN',
            key:'index',
            render : (text, record, index) => index+1
        },
        {
            title:"Operator Number",
            dataIndex:"operatorNumber",
            key:"operatorNumber",
            sorter: (a, b) => a.operatorNumber - b.operatorNumber,
            defaultSortOrder: 'ascend',
        },
        {
            title: "Operator Name",
            dataIndex: "operatorName",
            key: "operatorName"
        },
        {
            title: "Confirmed Amount",
            dataIndex: "confirmedAmount",
            key: "confirmedAmount",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Cancelled Amount",
            dataIndex: "cancelledAmount",
            key: "cancelledAmount",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Cancellation Penality",
            dataIndex: "cancellationPenality",
            key: "cancellationPenality",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title:"No of standard trips",
            dataIndex:"serviceCounts",
            key:"serviceCounts",
            render : (text, record, index) => {
                return (record.serviceCounts || 0) - (record.seatSharingServiceCount || 0);
            }
        },
        {
            title:"No of seat sharing trips",
            dataIndex:"seatSharingServiceCount",
            key:"seatSharingServiceCount"
        },
        {
            title: "SMS Count",
            dataIndex: "smsCount",
            key: "smsCount"
        },
        {
            title: "SMS Usage Charge",
            dataIndex: "smsUsageCharge",
            key: "smsUsageCharge",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Whatsapp Enabled",
            dataIndex: "whatsappEnabled",
            key: "whatsappEnabled",
            render : (text, record, index) => {
                return text==true?"Yes":"No";
            }
        },
        {
            title: "Whatsapp Count",
            dataIndex: "whatsAppSmsCount",
            key: "whatsAppSmsCount",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Whatsapp Usage Charge",
            dataIndex: "whatsappUsageCharge",
            key: "whatsappUsageCharge",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "No of GPS Devices",
            dataIndex: "noOfGpsDevices",
            key: "noOfGpsDevices",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "GPS Monthly Charge",
            dataIndex: "gpsMonthlyCharge",
            key: "gpsMonthlyCharge",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "No of Copilot Tabs",
            dataIndex: "noOfCopilotTabs",
            key: "noOfCopilotTabs",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Copilot Tabs Monthly Charge",
            dataIndex: "copilotTabsMonthlyCharge",
            key: "copilotTabsMonthlyCharge",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Confirmed Fare in Maven",
            dataIndex: "mavenConfirmedFare",
            key: "mavenConfirmedFare",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "50% of Maven penalty",
            dataIndex: "mavenCancellationPenality",
            key: "mavenCancellationPenality",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Confirmed Fare in OTAs",
            dataIndex: "otaConfirmedFare",
            key: "otaConfirmedFare",
            render : (text, record, index) => {
                return roundNumber(text);
            }
        },
        {
            title: "Driver app subscriptions",
            dataIndex: "driverSubscriptions",
            key: "driverSubscriptions",
        },
        {
            title: "No of Vechicles",
            dataIndex: "uniqueVechicleIds",
            key: "uniqueVechicleIds",
            render : (text, record, index) => {
                if(isValidArray(text)){
                    return text.length
                }else{
                    return 0
                }
            }
        },
        {
            title: "Total PNR Count",
            dataIndex: "pnrCount",
            key: "pnrCount"
        }

    ];

    // Other OTA partners present in the fetched data (maven stays in its own
    // legacy columns), each getting the same column pair as maven.
    const otherOtaPartnerCodes = useMemo(() => {
        const codes = [];
        workFlowSummary.forEach((row) => {
            if (isValid(row) && isValid(row.partnerActivity)) {
                Object.keys(row.partnerActivity).forEach((code) => {
                    if (code !== 'maven' && !codes.includes(code)) {
                        codes.push(code);
                    }
                });
            }
        });
        return codes.sort();
    }, [workFlowSummary]);

    const getPartnerCellValue = (record, code, field) => {
        return roundNumber((isValid(record.partnerActivity) && isValid(record.partnerActivity[code])
            && isValidNumber(record.partnerActivity[code][field])) ? record.partnerActivity[code][field] : 0);
    };

    const partnerColumns = [];
    otherOtaPartnerCodes.forEach((code) => {
        partnerColumns.push({
            title: `Confirmed Fare in ${code}`,
            key: `${code}ConfirmedFare`,
            render: (text, record) => getPartnerCellValue(record, code, 'confirmedFare')
        });
        partnerColumns.push({
            title: `50% of ${code} penalty`,
            key: `${code}CancellationPenality`,
            render: (text, record) => getPartnerCellValue(record, code, 'cancellationPenality')
        });
    });

    // Partner columns sit right after the maven pair, before "Confirmed Fare
    // in OTAs" - the summary row below inserts its cells at the same spot.
    const otaColIndex = columns.findIndex((column) => column.key === 'otaConfirmedFare');
    const allColumns = [...columns.slice(0, otaColIndex), ...partnerColumns, ...columns.slice(otaColIndex)];

  
      return (
          <div className="report-inner-div">
                <Form
                    onFinish={onFinish}
                    form={form}
                    >
                    <Row gutter={[16,16]}>
                        <Col>
                            <Form.Item name="dateRange" label="Date range" 
                            rules={[
                                { required: true , message: 'Please input your date range!'},
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      if (!value || (value[1].diff(value[0],"days") <= allowedMaxDaysRange)) {
                                        return Promise.resolve();
                                      }
                                      return Promise.reject(new Error(`Please select a date range with in ${allowedMaxDaysRange} days`));
                                    },
                                  })
                            ]}>
                                    <RangePicker 
                                        ranges={getRangeOptions(allowedMaxDaysRange)} 
                                    />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button htmlType="submit">Get</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                {showReport&&<Row gutter={[16,16]}>
                    <Col>
                        <Button onClick={()=>{
                            setIsExportModalVisible(true);
                        }}>Export</Button>
                    </Col>
                    <Col>
                        <Button
                            onClick={()=>{
                                setLoading(true);

                                pushUsage(filters.fromDate,filters.toDate,workFlowSummary,(msg)=>{
                                    console.log({msg});
                                    
                                    message.success(msg);
                                    setLoading(false);
                                },(errMsg)=>{
                                    message.error(errMsg);
                                    setLoading(false);
                                });
                            }}
                        >
                            Push Usage
                        </Button>
                    </Col>
                    <Col>
                        {/* <Button
                            onClick={()=>{
                                setLoading(true);
                                pushSettlementUsage(filters.fromDate,filters.toDate,workFlowSummary,(msg)=>{
                                    console.log({msg});
                                    
                                    message.success(msg);
                                    setLoading(false);
                                },(errMsg)=>{
                                    message.error(errMsg);
                                    setLoading(false);
                                });
                            }}
                        >
                            Push Monthly Settlement
                        </Button> */}
                    </Col>
                </Row>
                }
                <Modal
                    title="Export Invoice"
                    visible={isExportModalVisible}
                    onOk={() => {
                        if (!invoiceStartNumber || invoiceStartNumber.trim() === '') {
                            message.error('Please enter invoice start number');
                            return;
                        }
                        generateJsonToExport(filters, workFlowSummary, invoiceStartNumber.trim());
                        setIsExportModalVisible(false);
                        message.success('Export completed successfully');
                    }}
                    onCancel={() => {
                        setIsExportModalVisible(false);
                    }}
                    okText="Export"
                    cancelText="Cancel"
                >
                    <Form.Item label="Invoice Start Number" required>
                        <Input
                            placeholder="Enter invoice start number (e.g., INV1)"
                            value={invoiceStartNumber}
                            onChange={(e) => setInvoiceStartNumber(e.target.value)}
                            onPressEnter={() => {
                                if (invoiceStartNumber && invoiceStartNumber.trim() !== '') {
                                    generateJsonToExport(filters, workFlowSummary, invoiceStartNumber.trim());
                                    setIsExportModalVisible(false);
                                    message.success('Export completed successfully');
                                } else {
                                    message.error('Please enter invoice start number');
                                }
                            }}
                        />
                    </Form.Item>
                </Modal>
                <Row gutter={[16,16]}>
                    <Col>
                            <Table
                                columns={allColumns}
                                dataSource={workFlowSummary}
                                className="bordered-table"
                                pagination={false}
                                summary={(pageData ) => {
                                    let totalServiceCounts = 0;
                                    let totalSeatSharingServiceCount=0;
                                    let totalTripsWithoutSeatSharing = 0;
                                    let totalSmsCount = 0;
                                    let totalWhatsappCount = 0;
                                    let totalSmsUsageCharge = 0;
                                    let totalWhatsappUsageCharge = 0;
                                    let totalGpsMonthlyCharge = 0;
                                    let totalCopilotTabsMonthlyCharge = 0;
                                    let totalNoOfGpsDevices = 0;
                                    let totalNoOfCopilotTabs = 0;
                                    let totalCancelledAmount = 0;
                                    let totalConfirmedAmount = 0;
                                    let totalcancellationPenality = 0;
                                    let totalmavenConfirmedFare = 0 ;
                                    let totalmavenCancellationPenality = 0 ;
                                    let totalotaConfirmedFare = 0 ;
                                    let totalDriverSubscriptions = 0;
                                    let totalUniqueVechicleIds = 0;
                                    let totalPnrCount = 0;
                                    let totalsByPartner = {};
                                    otherOtaPartnerCodes.forEach((code) => {
                                        totalsByPartner[code] = { confirmedFare: 0, cancellationPenality: 0 };
                                    });
                                    pageData.forEach(({ whatsAppSmsCount, uniqueVechicleIds, mavenConfirmedFare, mavenCancellationPenality, otaConfirmedFare, partnerActivity, serviceCounts, driverSubscriptions, seatSharingServiceCount, smsCount, cancelledAmount, confirmedAmount, cancellationPenality, smsUsageCharge, whatsappUsageCharge, gpsMonthlyCharge, copilotTabsMonthlyCharge, noOfGpsDevices, noOfCopilotTabs, pnrCount }) => {
                                        totalServiceCounts+= serviceCounts || 0;
                                        totalSeatSharingServiceCount+= seatSharingServiceCount || 0;
                                        totalTripsWithoutSeatSharing+= (serviceCounts || 0) - (seatSharingServiceCount || 0);
                                        totalSmsCount+= smsCount || 0;
                                        totalWhatsappCount+= whatsAppSmsCount || 0;
                                        totalSmsUsageCharge+= smsUsageCharge || 0;
                                        totalWhatsappUsageCharge+= whatsappUsageCharge || 0;
                                        totalGpsMonthlyCharge+= gpsMonthlyCharge || 0;
                                        totalCopilotTabsMonthlyCharge+= copilotTabsMonthlyCharge || 0;
                                        totalNoOfGpsDevices+= noOfGpsDevices || 0;
                                        totalNoOfCopilotTabs+= noOfCopilotTabs || 0;
                                        totalCancelledAmount+= cancelledAmount || 0;
                                        totalConfirmedAmount+= confirmedAmount || 0;
                                        totalcancellationPenality+= cancellationPenality || 0;
                                        totalmavenConfirmedFare+= mavenConfirmedFare || 0;
                                        totalmavenCancellationPenality+= mavenCancellationPenality || 0;
                                        otherOtaPartnerCodes.forEach((code) => {
                                            if (isValid(partnerActivity) && isValid(partnerActivity[code])) {
                                                totalsByPartner[code].confirmedFare += Number(partnerActivity[code].confirmedFare) || 0;
                                                totalsByPartner[code].cancellationPenality += Number(partnerActivity[code].cancellationPenality) || 0;
                                            }
                                        });
                                        totalDriverSubscriptions+= driverSubscriptions || 0;
                                        totalotaConfirmedFare+= otaConfirmedFare || 0;
                                        totalPnrCount+= pnrCount || 0;
                                        if(isValidArray(uniqueVechicleIds)){
                                            totalUniqueVechicleIds = totalUniqueVechicleIds + uniqueVechicleIds.length;
                                        }
                                    });
                                    return (
                                        <>
                                          <Table.Summary.Row style= {{fontWeight: 'bold'}}>
                                            <Table.Summary.Cell colSpan= {2}></Table.Summary.Cell>
                                            <Table.Summary.Cell>Total</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalConfirmedAmount)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalCancelledAmount)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalcancellationPenality)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalTripsWithoutSeatSharing)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalSeatSharingServiceCount)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalSmsCount)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalSmsUsageCharge)}</Table.Summary.Cell>
                                            <Table.Summary.Cell></Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalWhatsappCount)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalWhatsappUsageCharge)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalNoOfGpsDevices)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalGpsMonthlyCharge)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalNoOfCopilotTabs)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalCopilotTabsMonthlyCharge)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalmavenConfirmedFare)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{roundNumber(totalmavenCancellationPenality)}</Table.Summary.Cell>
                                            {otherOtaPartnerCodes.map((code) => (
                                                <React.Fragment key={code}>
                                                    <Table.Summary.Cell>{roundNumber(totalsByPartner[code].confirmedFare)}</Table.Summary.Cell>
                                                    <Table.Summary.Cell>{roundNumber(totalsByPartner[code].cancellationPenality)}</Table.Summary.Cell>
                                                </React.Fragment>
                                            ))}
                                            <Table.Summary.Cell>{roundNumber(totalotaConfirmedFare)}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{totalDriverSubscriptions}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{totalUniqueVechicleIds}</Table.Summary.Cell>
                                            <Table.Summary.Cell>{totalPnrCount}</Table.Summary.Cell>
                                          </Table.Summary.Row>
                                        </>
                                      );
                                }}
                            />
                    </Col>
                </Row>
          </div>
      )
  }
            

      
